import React, { useEffect, useMemo } from 'react';
import { find, get, sortBy } from 'lodash';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';

import { statusLabels, chargerStatuses as chargerStatusesObj, GRAY_COLOR } from '../../common/constants/statuses';
import InfoButtonComponent from './components/InfoButtonComponent';
import { stopWatchStatistics, watchStatistics } from './actions';
import {
  selectAggregatedStatistics,
  selectConditionStatistics,
  selectConnectionStatistics,
  selectFaultsStatistics,
  selectStateStatistics,
} from './selectors';
import { changeFilter } from '../../common/components/SSPList/actions';
import { selectAppConfig } from '../../common/components/appConfig/selector';
import { selectLastFirstName, selectLoginFirstName } from '../Login/selectors';
import BarChartComponent from './components/BarChart';
import PieChartComponent from './components/PieChart';
import TotalEnergy from './components/TotalEnergy/TotalEnergy';

import conditionsIcon from '../../images/Charger Conditions.svg';
import onlineIcon from '../../images/Chargers Online.svg';
import statusIcon from '../../images/Charger Statuses.svg';
import { getShowHomeFlag } from '../../common/services/AuthenticationRules';

const CONDITIONS = {
  inactive: '#DCDCDC',
  active: '#30A980',
  pending: '#6daedf',
};

const chargerStatuses = (
  <div>
    Charger Statuses
    <InfoButtonComponent infoOption="chargerStatuses" isAbsolute={false} />
    <div className="subtitle">Active Online only</div>
  </div>
);

const chargersOnline = (
  <div>
    Chargers Online
    <div className="subtitle">Active only</div>
  </div>
);

const HomePage = () => {
  const dispatch = useDispatch();

  const navigate = useNavigate();

  const conditionStatistics = useSelector(selectConditionStatistics);
  const stateStatistics = useSelector(selectStateStatistics);
  const connectionStatistics = useSelector(selectConnectionStatistics);
  const config = useSelector(selectAppConfig);
  const firstName = useSelector(selectLoginFirstName);
  const lastName = useSelector(selectLastFirstName);
  const faultsStatistics = useSelector(selectFaultsStatistics);
  const aggregatedStatistics = useSelector(selectAggregatedStatistics);

  useEffect(() => {
    dispatch(watchStatistics());

    return () => {
      dispatch(stopWatchStatistics());
    };
  }, []);

  useEffect(() => {
    if (!getShowHomeFlag(config)) {
      navigate('/dashboard');
    }
  }, [config]);

  let totalConditionCount = 0;
  let totalStateCount = 0;
  let totalConnectionCount = 0;

  const conditionData = Object.keys(conditionStatistics)
    .map((key) => {
      totalConditionCount += conditionStatistics[key];

      return {
        data: key,
        name: key,
        value: conditionStatistics[key],
        color: CONDITIONS[key.toLowerCase()],
      };
    })
    .sort((a, b) => a.name.localeCompare(b.name));

  const stateData = sortBy(Object.keys(stateStatistics)).map((key) => {
    totalStateCount += stateStatistics[key];
    const color = get(find(chargerStatusesObj, item => item.key === key), 'color', GRAY_COLOR);

    return {
      data: key,
      name: statusLabels[key],
      value: stateStatistics[key],
      color,
    };
  });

  const connectionData = Object.keys(connectionStatistics).map((key) => {
    totalConnectionCount += connectionStatistics[key];

    return {
      data: key,
      name: key === 'true' ? 'Online' : 'Offline',
      value: connectionStatistics[key],
      color: key === 'true' ? '#30A980' : '#DCDCDC',
    };
  });

  const goToChargersList = (filterId, filterValue, isActive = false) => {
    navigate('/chargers/chargers_list');

    const filterBy = [
      {
        id: filterId,
        value: filterValue,
      },
    ];

    if (isActive) {
      filterBy.push({
        id: 'condition',
        value: 'active',
      });
    }

    dispatch(changeFilter('chargersList', filterBy));
  };

  const chartEventsCondition = useMemo(
    () => ({
      click: ({ data }) => {
        if (data) {
          goToChargersList('condition', data.data)
        }
      },
    }),
    [],
  );

  const chartEventsConnection = useMemo(
    () => ({ click: ({ data }) => {
      if (data) {
        goToChargersList('online', data.data, true)
      }
    }}),
    [],
  );

  const chartEventsState = useMemo(
    () => ({
      click: ({ data }) => {
        if (data) {
          goToChargersList('status', data.data.toUpperCase(), true)
        }
      },
    }),
    [],
  );

  return (
    <div className="home-page__wrapper">
      <h1 className="home-page__title">
        {`${firstName} ${lastName}, Welcome to ${get(config, 'customerName') || 'IoTecha'} Monitoring system!`}
      </h1>
      <div className="home-page__charts">
        <div className="home-page__charts--common">
          <TotalEnergy />
          <PieChartComponent
            data={conditionData}
            title="Charger Conditions"
            total={totalConditionCount}
            icon={conditionsIcon}
            chartEvents={chartEventsCondition}
            name="conditions"
          />
          <PieChartComponent
            data={connectionData}
            title={chargersOnline}
            total={totalConnectionCount}
            icon={onlineIcon}
            chartEvents={chartEventsConnection}
            name="online"
          />
          <PieChartComponent
            data={stateData}
            title={chargerStatuses}
            total={totalStateCount}
            icon={statusIcon}
            chartEvents={chartEventsState}
            name="statuses"
          />
        </div>
        <div className="home-page__charts--additional">
          <BarChartComponent dataStatistic={faultsStatistics} title="Faults" path="/reports/faults" />
          <BarChartComponent dataStatistic={aggregatedStatistics} title="Charging Sessions" path="/reports/sessions" />
        </div>
      </div>
    </div>
  );
};

export default HomePage;
